import { motion } from 'framer-motion';
import { useI18n } from '../lib/i18n';
import { TIDS } from '../constants/testIds';

const Hero = () => {
  const { t } = useI18n();

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-24 overflow-hidden bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <motion.span
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-block text-[#0071E3] text-xs sm:text-sm font-semibold tracking-[0.18em] uppercase"
            >
              {t.hero.eyebrow}
            </motion.span>

            <motion.h1
              data-testid={TIDS.heroHeadline}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-5 text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight font-extrabold text-[#1D1D1F]"
            >
              {t.hero.h1a}
              <br />
              <span className="text-[#0071E3]">{t.hero.h1b}</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-6 max-w-xl text-lg sm:text-xl leading-relaxed text-[#86868B] font-medium"
            >
              {t.hero.sub}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-9 flex flex-wrap items-center gap-3"
            >
              <button
                data-testid={TIDS.heroCtaPrimary}
                onClick={() => scrollTo('booking')}
                className="bg-[#0071E3] hover:bg-[#0077ED] text-white font-semibold rounded-full px-7 py-3.5 text-base transition-transform hover:scale-[1.03] active:scale-95 shadow-sm"
              >
                {t.hero.ctaPrimary}
              </button>
              <button
                data-testid={TIDS.heroCtaSecondary}
                onClick={() => scrollTo('memberships')}
                className="bg-[#F5F5F7] hover:bg-[#E8E8ED] text-[#1D1D1F] font-semibold rounded-full px-7 py-3.5 text-base transition-transform hover:scale-[1.03] active:scale-95"
              >
                {t.hero.ctaSecondary} <span className="ml-1">›</span>
              </button>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] w-full rounded-[2.5rem] overflow-hidden bg-[#F5F5F7]">
              <img
                src="https://images.pexels.com/photos/8408539/pexels-photo-8408539.jpeg"
                alt="Neatly folded clothes — premium laundry"
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white/30 via-transparent to-transparent" />
            </div>

            <div className="absolute -bottom-6 -left-6 sm:-left-10 bg-white rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.08)] px-5 py-4 border border-black/5">
              <p className="text-[11px] uppercase tracking-wider text-[#86868B] font-semibold">
                {t.hero.eyebrow}
              </p>
              <p className="text-sm font-semibold text-[#1D1D1F] mt-1">
                Pick up · Wash · Fold · Delivery
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
